import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { api } from "@/lib/api-client";
import {
  Button,
  Card,
  CardBody,
  Divider,
  Tooltip,
  cn,
  Skeleton,
  Link,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from "@heroui/react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { Eye, Trash, Download, Plus } from "lucide-react";
import { filesize } from "filesize";
import { AlertButton } from "@/Components/NextBase/AlertButton";
import { LimePagination } from "@/Components/NextBase/LimePagination";
import { useSearchParams } from "react-router";
import { CustomerFileUploadForm } from "@/Components/NextBase/CustomerFileUploadForm";

export const CustomerFiles = ({
  customerId,
  userAppointmentId,
}: {
  customerId: number;
  userAppointmentId?: number;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    isOpen: isUploadModalOpen,
    onOpen: onUploadModalOpen,
    onClose: onUploadModalClose,
  } = useDisclosure();
  const page = searchParams.has("page")
    ? parseInt(searchParams.get("page")!)
    : 1;
  const perPage = searchParams.has("perPage")
    ? searchParams.get("perPage")!
    : "10";

  const setPage = (page: number) => {
    setSearchParams((prev) => {
      prev.set("page", page.toString());
      return prev;
    });
  };

  const setPerPage = (perPage: string) => {
    setSearchParams((prev) => {
      prev.set("perPage", perPage);
      prev.set("page", "1");
      return prev;
    });
  };

  const {
    data,
    isFetching,
    refetch,
    processedErrorMessage: fetchError,
  } = api.storage.useGetCustomerFiles({
    customerId,
    query: {
      page,
      limit: parseInt(perPage),
      userAppointmentId,
    },
  });

  const {
    mutateAsync: deleteCustomerFile,
    isPending: isDeletingCustomerFile,
    processedErrorMessage: deleteCustomerFileError,
  } = api.storage.useDeleteCustomerFile();

  const {
    mutateAsync: getCustomerFileDownloadPresignedUrl,
    isPending: isGettingCustomerFileDownloadPresignedUrl,
    processedErrorMessage: getCustomerFileDownloadPresignedUrlError,
  } = api.storage.useGetCustomerFileDownloadPresignedUrl();

  const handleDownloadFile = async (fileId: number) => {
    const presignedUrlData = await getCustomerFileDownloadPresignedUrl({
      id: customerId,
      fileId,
      query: {
        asAttachment: true,
      },
    });

    // Create an ephemeral <a> to force download
    const link = document.createElement("a");
    link.href = presignedUrlData.presignedUrl;

    // 'download' attribute will make the browser save the file
    // You can specify a filename here:
    link.download = presignedUrlData.fileName ?? "download";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handlePreviewFile = async (fileId: number) => {
    const presignedUrlData = await getCustomerFileDownloadPresignedUrl({
      id: customerId,
      fileId,
    });

    window.open(presignedUrlData.presignedUrl, "_blank");
  };

  const files = (() => {
    if (isFetching) {
      return Array.from({ length: Number(perPage) }, (_, i) => ({
        id: -1 - i,
        name: "",
        size: 0,
        state: { raw: "uploaded", label: t`Naložena` },
        label: "",
        uploaderName: "",
        dateCreated: "",
        isSkeleton: true,
        appointment: undefined,
      }));
    }

    if (!data) {
      return [];
    }

    return data.files.map((file) => ({
      ...file,
      isSkeleton: false,
    }));
  })();

  console.log("FIles", files);

  return (
    <>
      <div className="mt-4">
        <LimeAlert message={fetchError} className="mb-2" />

        {files.length !== 0 ? (
          <div className="mt-2 flex flex-col gap-4">
            {files.map((file) => {
              return (
                <Skeleton
                  isLoaded={!file.isSkeleton}
                  className="rounded-2xl"
                  key={file.id}
                >
                  <Card
                    shadow="sm"
                    className={cn({
                      "outline-2 outline-red-500": file.state.raw === "failed",
                    })}
                  >
                    <CardBody>
                      <div>
                        <div>
                          <p className="mb-1 font-medium leading-5">
                            {file.label ?? file.name}
                            {file.label != null && (
                              <span className="ml-1 text-xs font-normal text-gray-500">
                                ({file.name})
                              </span>
                            )}
                          </p>

                          <div className="flex items-end justify-between gap-1">
                            <div>
                              {file.state.raw === "uploaded" && (
                                <span className="text-xs text-gray-500">
                                  {filesize(file.size)}
                                </span>
                              )}
                              <p className="text-xs text-gray-500">
                                <Trans>Naložil/a</Trans>: {file.uploaderName}
                              </p>
                              <p className="text-xs text-gray-500">
                                <Trans>Datum</Trans>: {file.dateCreated}
                              </p>
                              {userAppointmentId == null &&
                                file.appointment != null && ( // only show appointment link if not on appt modal
                                  <Link
                                    className="text-xs"
                                    showAnchorIcon
                                    href={`/dashboard/calendar?openApptId=${file.appointment?.appointmentId}`}
                                  >
                                    <Trans>Termin</Trans>:{" "}
                                    {file.appointment?.startTime}
                                  </Link>
                                )}
                            </div>

                            <div className="flex gap-2">
                              <Tooltip content={t`Predogled datoteke`}>
                                <Button
                                  variant="light"
                                  isIconOnly
                                  isLoading={
                                    isGettingCustomerFileDownloadPresignedUrl
                                  }
                                  size="lg"
                                  className="hidden md:flex"
                                  onPress={async () => {
                                    await handlePreviewFile(file.id);
                                  }}
                                >
                                  <Eye size={16} />
                                </Button>
                              </Tooltip>

                              <LimeAlert
                                message={
                                  getCustomerFileDownloadPresignedUrlError
                                }
                              />

                              <Tooltip content={t`Prenesi datoteko`}>
                                <Button
                                  variant="light"
                                  isLoading={
                                    isGettingCustomerFileDownloadPresignedUrl
                                  }
                                  isIconOnly
                                  size="lg"
                                  onPress={async () => {
                                    await handleDownloadFile(file.id);
                                  }}
                                >
                                  <Download size={16} />
                                </Button>
                              </Tooltip>

                              <AlertButton
                                title={t`Izbriši datoteko`}
                                content={t`Ali ste prepričani, da želite izbrisati datoteko?`}
                                tooltip={t`Izbriši datoteko`}
                                buttonProps={{
                                  variant: "light",
                                  startContent: <Trash size={16} />,
                                  color: "danger",
                                  isIconOnly: true,
                                  size: "lg",
                                }}
                                confirmProps={{
                                  label: t`Izbriši datoteko`,
                                  onClick: async () => {
                                    await deleteCustomerFile({
                                      id: customerId,
                                      fileId: file.id,
                                    });

                                    refetch();
                                  },
                                  isLoading: isDeletingCustomerFile,
                                }}
                              ></AlertButton>
                            </div>
                          </div>
                        </div>
                      </div>

                      {file.state.raw !== "uploaded" && (
                        <p
                          className={cn("text-xs text-gray-500", {
                            "font-medium text-red-500":
                              file.state.raw === "failed",
                            "text-sm font-medium text-warning-600":
                              file.state.raw === "pending",
                          })}
                        >
                          {file.state.label}
                        </p>
                      )}
                    </CardBody>
                  </Card>
                </Skeleton>
              );
            })}

            <div className="mt-4 flex justify-end">
              <Button
                onPress={onUploadModalOpen}
                color="primary"
                startContent={<Plus size={16} />}
              >
                <Trans>Naloži datoteko</Trans>
              </Button>
            </div>

            <LimePagination
              paginationProps={{
                total: data?.pagination.totalPages ?? 1,
                page,
                onChange: setPage,
              }}
              perPageProps={{
                label: t`datotek`,
                values: [5, 10, 20],
                onChange: setPerPage,
                value: perPage,
              }}
            />
          </div>
        ) : (
          <>
            <p className="text-center text-sm text-gray-500">
              <Trans>Stranka še nima datotek</Trans>
            </p>
            <div className="mt-4 flex justify-end">
              <Button
                onPress={onUploadModalOpen}
                color="primary"
                startContent={<Plus size={16} />}
              >
                <Trans>Naloži datoteko</Trans>
              </Button>
            </div>
          </>
        )}
      </div>

      <Modal isOpen={isUploadModalOpen} onClose={onUploadModalClose}>
        <ModalContent>
          <ModalHeader>
            <Trans>Nalaganje datotek</Trans>
          </ModalHeader>
          <ModalBody>
            <CustomerFileUploadForm
              customerId={customerId}
              userAppointmentId={userAppointmentId}
              onSuccessfulUpload={() => {
                refetch();
              }}
            />
          </ModalBody>
          <ModalFooter />
        </ModalContent>
      </Modal>
    </>
  );
};
